/**
 * Open Science makes `!` a direct execution affordance. Cadence keeps the
 * compact syntax, but the prefix is only a routing instruction: the command
 * body is sent to the authorized Runtime/Sandbox contract, never to the
 * browser or a local shell.
 */
export interface ControlledCommandInput {
  /** An explicit Runtime Agent selected by the researcher, when present. */
  agentId?: string;
  /** The command body after the Composer-only routing markers are removed. */
  command: string;
}

/**
 * Accept both natural directed forms: `@agent !command` and `! @agent command`.
 * A bang in ordinary prose is deliberately not a command trigger.
 */
export function controlledCommandInput(
  value: string,
): ControlledCommandInput | undefined {
  const directedFirst = /^\s*@([a-z0-9_-]+)\s+!\s*([\s\S]*)$/iu.exec(value);
  if (directedFirst) {
    return { agentId: directedFirst[1], command: directedFirst[2] };
  }

  const commandFirst = /^\s*!\s*(?:@([a-z0-9_-]+)\s+)?([\s\S]*)$/iu.exec(value);
  if (!commandFirst) return undefined;
  return { agentId: commandFirst[1], command: commandFirst[2] };
}

/**
 * Returns an ordinary Composer request while preserving an explicitly chosen
 * Agent. Removing the `!` mode must not silently drop the user's route, but it
 * must also never leave a command marker that would re-enter Sandbox mode.
 */
export function withoutControlledCommandMarker(value: string): string {
  const directedFirst = /^(\s*@[a-z0-9_-]+\s+)!\s*/iu.exec(value);
  if (directedFirst) {
    return `${directedFirst[1]}${value.slice(directedFirst[0].length)}`;
  }

  const commandFirst = /^\s*!\s*(?:@([a-z0-9_-]+)\s+)?/iu.exec(value);
  if (!commandFirst) return value;
  const route = commandFirst[1] ? `@${commandFirst[1]} ` : "";
  return `${route}${value.slice(commandFirst[0].length)}`;
}
